'use client'

import { createContext, useContext, type MouseEvent, type ReactNode } from 'react'
import { Check, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@elmnts/ui/button'
import { useNotificationActions } from '@/hooks/notifications/useNotificationActions'
import type { NotificationItem } from '@/services/notifications/types'
import { getKindStyle } from './kind-style'
import { formatTargetSummary } from './format-target'
import { formatAbsoluteFromEpoch, formatRelativeFromEpoch } from './format-time'

const NotificationContext = createContext<NotificationItem | null>(null)

function useNotificationCtx(): NotificationItem {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('Primitivas de Notification exigem um <Notification> acima')
  return ctx
}

interface RootProps {
  notification: NotificationItem
  children: ReactNode
  className?: string
  /** Força o wrapper `<div>` mesmo sem className. */
  asWrapper?: boolean
}

/** Provider do item: as primitivas leem daqui em vez de receber props repetidas. */
function Root({ notification, children, className, asWrapper }: RootProps) {
  const content =
    asWrapper || className ? <div className={className}>{children}</div> : <>{children}</>
  return <NotificationContext.Provider value={notification}>{content}</NotificationContext.Provider>
}

function KindIcon({ className }: { className?: string }) {
  const n = useNotificationCtx()
  const style = getKindStyle(n.kind)
  const Icon = style.icon
  return <Icon className={cn('size-4 shrink-0', style.iconClass, className)} />
}

function KindBadge({ className }: { className?: string }) {
  const n = useNotificationCtx()
  const style = getKindStyle(n.kind)
  return (
    <span
      className={cn(
        'inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        style.badgeClass,
        className,
      )}
    >
      {style.label}
    </span>
  )
}

function Title({
  className,
  as: Component = 'h3',
}: {
  className?: string
  as?: 'h1' | 'h2' | 'h3' | 'h4'
}) {
  const n = useNotificationCtx()
  return (
    <Component className={cn('text-sm font-medium leading-tight break-words', className)}>
      {n.title}
    </Component>
  )
}

function Body({ preview = false, className }: { preview?: boolean; className?: string }) {
  const n = useNotificationCtx()
  if (!n.body) return null
  return preview ? (
    <p className={cn('line-clamp-1 text-xs text-muted-foreground break-words', className)}>
      {n.body}
    </p>
  ) : (
    <p className={cn('whitespace-pre-wrap text-sm text-foreground break-words', className)}>
      {n.body}
    </p>
  )
}

function Time({ absolute = false, className }: { absolute?: boolean; className?: string }) {
  const n = useNotificationCtx()
  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      {absolute ? formatAbsoluteFromEpoch(n.created_at) : formatRelativeFromEpoch(n.created_at)}
    </span>
  )
}

function Target({ className }: { className?: string }) {
  const n = useNotificationCtx()
  return <div className={cn('text-xs text-muted-foreground', className)}>{formatTargetSummary(n.target)}</div>
}

function Meta({ className }: { className?: string }) {
  const n = useNotificationCtx()
  return (
    <div
      className={cn(
        'space-y-0.5 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground',
        className,
      )}
    >
      <div>{formatTargetSummary(n.target)}</div>
      <div>
        Tipo: <code className="font-mono">{n.type_id}</code>
        {n.end_time && <>{' · expira '}{formatRelativeFromEpoch(n.end_time)}</>}
      </div>
    </div>
  )
}

export interface ActionProps {
  variant?: 'icon' | 'labeled'
  className?: string
  onSuccess?: () => void
}

function ReadAction({ variant = 'icon', className, onSuccess }: ActionProps) {
  const n = useNotificationCtx()
  const { markRead } = useNotificationActions()
  // stopPropagation/preventDefault: o card inteiro costuma ser um <Link>.
  const handle = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    markRead.mutate({ ids: [n.id] }, { onSuccess })
  }

  if (variant === 'icon') {
    return (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={cn('h-7 px-2', className)}
        onClick={handle}
        disabled={markRead.isPending}
        title="Marcar como lida"
        aria-label="Marcar como lida"
      >
        <Check className="size-3.5" />
      </Button>
    )
  }
  return (
    <Button type="button" onClick={handle} disabled={markRead.isPending} className={className}>
      <Check className="mr-1 size-4" />
      Marcar como lida
    </Button>
  )
}

function DeleteAction({ variant = 'icon', className, onSuccess }: ActionProps) {
  const n = useNotificationCtx()
  const { remove } = useNotificationActions()
  const handle = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    remove.mutate({ ids: [n.id] }, { onSuccess })
  }

  if (variant === 'icon') {
    return (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className={cn('h-7 px-2 text-muted-foreground hover:text-destructive', className)}
        onClick={handle}
        disabled={remove.isPending}
        title="Dispensar"
        aria-label="Dispensar"
      >
        <Trash2 className="size-3.5" />
      </Button>
    )
  }
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={handle}
      disabled={remove.isPending}
      className={className}
    >
      <Trash2 className="mr-1 size-4" />
      Dispensar
    </Button>
  )
}

export const Notification = Object.assign(Root, {
  KindIcon,
  KindBadge,
  Title,
  Body,
  Time,
  Target,
  Meta,
  ReadAction,
  DeleteAction,
})
